/**
 * Impacto financiero de una investigacion: los montos del quebranto y las
 * cuentas por las que se movio el dinero. Vive en `folder.financial_impact` y
 * se llena a mano, tipicamente en casos de fraude.
 *
 * Los montos se guardan como string y no como numero a proposito: el folder se
 * cifra con KMS recorriendo el dict, y ese recorrido solo cifra strings (los
 * numeros pasan de largo y quedarian en claro).
 */

export const FINANCIAL_IMPACT_CURRENCIES = ["MXN", "USD", "EUR"];

export const DEFAULT_FINANCIAL_IMPACT_CURRENCY = "MXN";

export const FINANCIAL_IMPACT_AMOUNT_FIELDS = [
  "potential_amount",
  "final_amount",
  "recovered_amount",
];

/**
 * De donde salieron los recursos y a donde se fueron. Es el eje del registro:
 * una cuenta afectada es el origen del quebranto y una beneficiaria el destino.
 */
export const ACCOUNT_FLOWS = {
  AFFECTED: "affected",
  BENEFICIARY: "beneficiary",
};

export const ACCOUNT_TYPES = {
  ACCOUNT: "account",
  CLABE: "clabe",
  CARD: "card",
  OTHER: "other",
};

const LOCALE_BY_LANG = { es: "es-MX", en: "en-US" };

const getLocale = () => LOCALE_BY_LANG[localStorage.getItem("lang")] ?? "es-MX";

/**
 * Numero o null. Un string vacio, un texto o un NaN cuentan como "sin dato",
 * que no es lo mismo que un monto de cero.
 */
export const parseAmount = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(number) ? number : null;
};

/** Deja el string que se guarda en el folder: dos decimales, sin separadores. */
export const serializeAmount = (value) => {
  const number = parseAmount(value);
  return number === null ? null : number.toFixed(2);
};

/** Solo el numero, para mostrarlo dentro de un input que ya trae el simbolo. */
export const formatAmountNumber = (value) => {
  const number = parseAmount(value);
  if (number === null) return "";
  return new Intl.NumberFormat(getLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

/** Monto con su moneda, para las vistas de solo lectura. */
export const formatAmountWithCurrency = (
  value,
  currency = DEFAULT_FINANCIAL_IMPACT_CURRENCY
) => {
  const number = parseAmount(value);
  if (number === null) return "";
  try {
    return new Intl.NumberFormat(getLocale(), {
      style: "currency",
      currency: currency || DEFAULT_FINANCIAL_IMPACT_CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  } catch {
    // Moneda desconocida: al menos el numero sale bien formateado.
    return `${formatAmountNumber(number)} ${currency ?? ""}`.trim();
  }
};

/**
 * Lo que el usuario puede teclear en un campo de monto: digitos, un punto y
 * hasta dos decimales. Se aplica en cada tecla para no dejar entrar basura.
 */
export const sanitizeAmountInput = (value) => {
  const onlyValid = String(value ?? "").replace(/[^0-9.]/g, "");
  const [integer, ...decimals] = onlyValid.split(".");
  if (decimals.length === 0) return integer;
  return `${integer}.${decimals.join("").slice(0, 2)}`;
};

/**
 * Numeros de cuenta, CLABE y tarjetas: digitos y separadores, nada mas. No se
 * valida el largo porque tambien se registran cuentas de bancos extranjeros.
 */
export const sanitizeAccountNumber = (value) =>
  String(value ?? "")
    .replace(/[^0-9A-Za-z\-\s]/g, "")
    .slice(0, 40);

export const emptyAccount = () => ({
  flow: ACCOUNT_FLOWS.AFFECTED,
  account_type: ACCOUNT_TYPES.ACCOUNT,
  institution: "",
  account_number: "",
  holder: "",
  amount: null,
  details: "",
});

export const normalizeAccount = (account) => {
  const base = emptyAccount();
  if (!account || typeof account !== "object") return base;

  return {
    flow:
      account.flow === ACCOUNT_FLOWS.BENEFICIARY
        ? ACCOUNT_FLOWS.BENEFICIARY
        : ACCOUNT_FLOWS.AFFECTED,
    account_type: Object.values(ACCOUNT_TYPES).includes(account.account_type)
      ? account.account_type
      : ACCOUNT_TYPES.ACCOUNT,
    institution: account.institution ?? "",
    account_number: account.account_number ?? "",
    holder: account.holder ?? "",
    amount: parseAmount(account.amount),
    details: account.details ?? "",
  };
};

/** Estructura vacia, la que usa el formulario cuando no hay nada capturado. */
export const emptyFinancialImpact = () => ({
  currency: DEFAULT_FINANCIAL_IMPACT_CURRENCY,
  potential_amount: null,
  final_amount: null,
  recovered_amount: null,
  origin: "",
  destination: "",
  notes: "",
  accounts: [],
});

/** Acepta lo que venga del API (ya descifrado) y lo ordena. */
export const normalizeFinancialImpact = (impact) => {
  const base = emptyFinancialImpact();
  if (!impact || typeof impact !== "object") return base;

  return {
    currency: impact.currency || DEFAULT_FINANCIAL_IMPACT_CURRENCY,
    potential_amount: parseAmount(impact.potential_amount),
    final_amount: parseAmount(impact.final_amount),
    recovered_amount: parseAmount(impact.recovered_amount),
    origin: impact.origin ?? "",
    destination: impact.destination ?? "",
    notes: impact.notes ?? "",
    accounts: Array.isArray(impact.accounts)
      ? impact.accounts.map(normalizeAccount)
      : [],
  };
};

/** Una cuenta sin numero ni titular ni institucion no aporta nada. */
export const isAccountEmpty = (account) => {
  const { institution, account_number, holder, amount, details } =
    normalizeAccount(account);
  return (
    !institution.trim() &&
    !account_number.trim() &&
    !holder.trim() &&
    amount === null &&
    !details.trim()
  );
};

/** Sin montos, sin texto y sin cuentas no hay nada que documentar. */
export const isFinancialImpactEmpty = (impact) => {
  const normalized = normalizeFinancialImpact(impact);
  const hasAmount = FINANCIAL_IMPACT_AMOUNT_FIELDS.some(
    (field) => normalized[field] !== null
  );
  const hasText = [
    normalized.origin,
    normalized.destination,
    normalized.notes,
  ].some((text) => text.trim());

  return !hasAmount && !hasText && normalized.accounts.length === 0;
};

/**
 * Perdida neta = monto final - monto recuperado. Sin monto final no hay nada
 * que restar; sin recuperado, la perdida es el monto final completo.
 */
export const getNetLoss = (impact) => {
  const { final_amount, recovered_amount } = normalizeFinancialImpact(impact);
  if (final_amount === null) return null;
  return final_amount - (recovered_amount ?? 0);
};

export const getAccountsByFlow = (accounts, flow) =>
  (Array.isArray(accounts) ? accounts : []).filter(
    (account) => normalizeAccount(account).flow === flow
  );

/**
 * Suma de los importes capturados en las cuentas de un flujo. Devuelve null
 * cuando ninguna cuenta trae importe, para no pintar un total de cero falso.
 */
export const getAccountsTotal = (accounts, flow) => {
  const list = flow ? getAccountsByFlow(accounts, flow) : accounts ?? [];
  const amounts = list
    .map((account) => normalizeAccount(account).amount)
    .filter((amount) => amount !== null);

  if (amounts.length === 0) return null;
  return amounts.reduce((total, amount) => total + amount, 0);
};

/**
 * Lo que se manda al API dentro de `folder.financial_impact`. Siempre devuelve
 * el objeto, aunque quede vacio: el PATCH interpreta un null como "no lo
 * mandaron" y conservaria lo anterior, asi que vaciar la seccion no serviria
 * de nada. Un objeto vacio si se guarda, y al leerlo la seccion vuelve a su
 * estado inicial.
 */
export const serializeFinancialImpact = (impact) => {
  const normalized = normalizeFinancialImpact(impact);
  return {
    currency: normalized.currency,
    potential_amount: serializeAmount(normalized.potential_amount),
    final_amount: serializeAmount(normalized.final_amount),
    recovered_amount: serializeAmount(normalized.recovered_amount),
    origin: normalized.origin.trim(),
    destination: normalized.destination.trim(),
    notes: normalized.notes.trim(),
    accounts: normalized.accounts.map((account) => ({
      flow: account.flow,
      account_type: account.account_type,
      institution: account.institution.trim(),
      account_number: account.account_number.trim(),
      holder: account.holder.trim(),
      amount: serializeAmount(account.amount),
      details: account.details.trim(),
    })),
  };
};
